import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EkycStatus } from '../../../core/enum/ekyc-status.enum';
import { IdentificationType } from '../../../core/enum/identification-type.enum';
import { Role } from '../../../core/enum/role.enum';
import { UserProfile } from '../../../core/models/user/user-profile';
import { ICMaskFormat, PassportFormat } from '../../../core/services/constant';
import { FormValidators } from '../../../core/validator/form.validators';

// INC-289 Phase 1: identity fields (userIdentityNoType, identityNo, fullName,
// birthday) extracted verbatim from profile-details.component.ts's buildForm()
// / changeIdentificationType() so the standalone settings page and the
// Phase-2 review-attest edit sheet share ONE copy of the validators and error
// messages. Not part of this component: the eKYC status badge, profile
// photo upload, and the READONLY email field — those stay on the page.
@Component({
  standalone: false,
  selector: 'app-identity-section-form',
  templateUrl: './identity-section-form.component.html',
  styleUrls: ['./identity-section-form.component.scss'],
})
export class IdentitySectionFormComponent implements OnInit, OnChanges {
  @Input() profile: UserProfile;
  @Input() role: Role;
  @Input() showFormError = false;

  form: UntypedFormGroup;

  icMaskFormat = ICMaskFormat;
  minDob: Date = new Date(1900, 0, 1);
  today = new Date();
  IdentificationType: typeof IdentificationType = IdentificationType;
  EkycStatus: typeof EkycStatus = EkycStatus;
  Role: typeof Role = Role;

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Guarded on `this.form` since the very first change (the one Angular
    // fires before ngOnInit) arrives before buildForm() has run; ngOnInit
    // does that initial seed instead.
    if (changes.profile && this.profile && this.form) {
      this.seedFromProfile();
    }
  }

  buildForm(): void {
    this.form = this.formBuilder.group({
      userIdentityNoType: [IdentificationType.MyKad, Validators.required],
      identityNo: ['', Validators.required],
      fullName: ['', Validators.required],
      birthday: [
        null,
        this.role != Role.CompanyRep ? Validators.required : null,
        FormValidators.minYearValidator(1900),
      ],
    });

    if (this.profile) {
      this.seedFromProfile();
    }
  }

  private seedFromProfile(): void {
    this.form.patchValue(this.profile);
    this.changeIdentificationType();
  }

  changeIdentificationType(): void {
    let type = this.form.value.userIdentityNoType;

    if (type != IdentificationType.Passport) {
      this.form.controls.identityNo.setValidators([Validators.required]);
      this.form.controls.identityNo.updateValueAndValidity();
    } else {
      this.form.controls.identityNo.setValidators([
        Validators.required,
        Validators.pattern(PassportFormat),
      ]);
      this.form.controls.identityNo.updateValueAndValidity();
    }
  }

  // No explicit return type: mirrors form.controls.userIdentityNoType.value's
  // natural `any` (UntypedFormGroup) so `IdentificationType[...]` reverse
  // lookups and `!= IdentificationType.Passport` comparisons in the template
  // (moved verbatim from profile-details.component.html) type-check.
  getUserIdentityNoType() {
    return this.form.controls.userIdentityNoType.value;
  }
}
